import { useState, useEffect, useContext } from 'react';
import { AppContext } from '../App';
import api from '../api';
import { useI18n } from '../i18n/index.jsx';

export default function HREmployees() {
  const { setPage, currentCompany } = useContext(AppContext);
  const [employees, setEmployees] = useState([]);
  const { t } = useI18n();
  const [contracts, setContracts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showAddEmp, setShowAddEmp] = useState(false);
  const [showAddContract, setShowAddContract] = useState(false);
  const [showPayroll, setShowPayroll] = useState(false);
  const [payrollData, setPayrollData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [empForm, setEmpForm] = useState({ first_name: '', last_name: '', pesel: '', email: '', phone: '', hire_date: '' });
  const [contractForm, setContractForm] = useState({
    contract_type: 'umowa_o_prace', position: '', gross_salary: '', start_date: '', end_date: '', kup_rate: 20, zus_sickness: true
  });

  const CONTRACT_LABELS = {
    umowa_o_prace: { icon: '📋', label: t('hr.contract_umowa_o_prace'), zus: true },
    umowa_zlecenie: { icon: '📝', label: t('hr.contract_umowa_zlecenie'), zus: true },
    umowa_o_dzielo: { icon: '🎨', label: t('hr.contract_umowa_o_dzielo'), zus: false },
    kontrakt_menedzerski: { icon: '💼', label: t('hr.contract_kontrakt_menedzerski'), zus: true },
    b2b: { icon: '🏢', label: t('hr.contract_b2b'), zus: false },
  };

  useEffect(() => { load(); }, [currentCompany?.id]);

  async function load() {
    try {
      const cid = currentCompany?.id;
      const qs = cid ? `?company_id=${cid}` : '';
      const [empRes, conRes] = await Promise.all([
        api.get(`/hr/employees${qs}`),
        api.get(`/hr/contracts${qs}`),
      ]);
      setEmployees(empRes.data);
      setContracts(conRes.data);
    } catch (e) {
      setError(e.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  }

  async function addEmployee() {
    try {
      const cid = currentCompany?.id;
      const qs = cid ? `?company_id=${cid}` : '';
      await api.post(`/hr/employees${qs}`, empForm);
      setShowAddEmp(false);
      setEmpForm({ first_name: '', last_name: '', pesel: '', email: '', phone: '', hire_date: '' });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || t('common.error'));
    }
  }

  async function addContract() {
    if (!selected) return;
    try {
      const cid = currentCompany?.id;
      const qs = cid ? `?company_id=${cid}` : '';
      await api.post(`/hr/contracts${qs}`, {
        ...contractForm,
        employee_id: selected.id,
        gross_salary: parseFloat(contractForm.gross_salary),
        end_date: contractForm.end_date || null,
      });
      setShowAddContract(false);
      await load();
      const cidParam2 = currentCompany?.id ? `?company_id=${currentCompany.id}` : '';
      const res = await api.get(`/hr/employees/${selected.id}${cidParam2}`);
      setSelected(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || t('common.error'));
    }
  }

  async function generatePayroll() {
    const now = new Date();
    try {
      const cid = currentCompany?.id;
      const qs = cid ? `?company_id=${cid}` : '';
      const res = await api.post(`/hr/payroll/generate${qs}`, { year: now.getFullYear(), month: now.getMonth() + 1 });
      setPayrollData(res.data);
      setShowPayroll(true);
    } catch (e) {
      setError(e.response?.data?.detail || t('common.error'));
    }
  }

  async function loadPayroll() {
    const now = new Date();
    try {
      const cid = currentCompany?.id;
      const cidParam = cid ? `&company_id=${cid}` : '';
      const res = await api.get(`/hr/payroll?year=${now.getFullYear()}&month=${now.getMonth() + 1}${cidParam}`);
      setPayrollData(res.data);
      setShowPayroll(true);
    } catch (e) {
      setError(e.response?.data?.detail || t('common.error'));
    }
  }

  async function selectEmployee(emp) {
    try {
      const cid = currentCompany?.id;
      const cidParam = cid ? `?company_id=${cid}` : '';
      const res = await api.get(`/hr/employees/${emp.id}${cidParam}`);
      setSelected(res.data);
    } catch (e) {
      setSelected(emp);
    }
  }

  if (loading) return <div className="page"><div className="spinner" /></div>;

  return (
    <div className="page">
      <button className="btn btn-link back-btn" onClick={() => setPage('dashboard')}>← {t('common.back')}</button>
      <h1>👥 {t('hr.title')}</h1>

      {error && <div className="banner banner-warning" style={{ marginBottom: 12 }}>⚠️ {error}</div>}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button className="btn btn-primary" onClick={() => setShowAddEmp(true)}>+ {t('hr.add_employee')}</button>
        <button className="btn btn-secondary" onClick={generatePayroll}>📊 {t('hr.calculate_payroll')}</button>
        <button className="btn btn-secondary" onClick={loadPayroll}>📋 {t('hr.payroll_sheet')}</button>
        <button className="btn btn-secondary" onClick={() => setPage('hr_leaves')}>🏖️ {t('hr.leaves')}</button>
      </div>

      {/* Add Employee */}
      {showAddEmp && (
        <div className="card" style={{ marginBottom: 16, padding: 16 }}>
          <h3>➕ {t('hr.new_employee')}</h3>
          <div className="form">
            <div className="field"><label>{t('hr.first_name')} *</label><input value={empForm.first_name} onChange={e => setEmpForm(p => ({...p, first_name: e.target.value}))} /></div>
            <div className="field"><label>{t('hr.last_name')} *</label><input value={empForm.last_name} onChange={e => setEmpForm(p => ({...p, last_name: e.target.value}))} /></div>
            <div className="field"><label>{t('hr.pesel')}</label><input value={empForm.pesel} onChange={e => setEmpForm(p => ({...p, pesel: e.target.value}))} /></div>
            <div className="field"><label>{t('common.email')}</label><input type="email" value={empForm.email} onChange={e => setEmpForm(p => ({...p, email: e.target.value}))} /></div>
            <div className="field"><label>{t('common.phone')}</label><input value={empForm.phone} onChange={e => setEmpForm(p => ({...p, phone: e.target.value}))} /></div>
            <div className="field"><label>{t('hr.hire_date')}</label><input type="date" value={empForm.hire_date} onChange={e => setEmpForm(p => ({...p, hire_date: e.target.value}))} /></div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn btn-primary" onClick={addEmployee}>✅ {t('hr.add_btn')}</button>
            <button className="btn btn-secondary" onClick={() => setShowAddEmp(false)}>{t('common.cancel')}</button>
          </div>
        </div>
      )}

      {/* Employees List */}
      <div className="list">
        {employees.map(emp => {
          const empContracts = contracts.filter(c => c.employee_id === emp.id);
          const activeContract = empContracts.find(c => c.status === 'active');
          return (
            <div key={emp.id} className="list-item" onClick={() => selectEmployee(emp)} style={{ cursor: 'pointer' }}>
              <div className="list-main">
                <div className="list-title">{emp.first_name} {emp.last_name}</div>
                <div className="list-sub">
                  {activeContract
                    ? `${CONTRACT_LABELS[activeContract.contract_type]?.icon || '📄'} ${CONTRACT_LABELS[activeContract.contract_type]?.label || activeContract.contract_type} • ${activeContract.gross_salary} PLN`
                    : `⚠️ ${t('hr.no_active_contract')}`
                  }
                </div>
              </div>
              <div className={`status-badge status-${emp.status}`}>{emp.status}</div>
            </div>
          );
        })}
        {employees.length === 0 && <div className="empty">{t('hr.no_employees')}</div>}
      </div>

      {/* Selected Employee Detail */}
      {selected && (
        <div className="card" style={{ marginTop: 16, padding: 16 }}>
          <h3>👤 {selected.first_name} {selected.last_name}</h3>
          {selected.pesel && <div style={{ fontSize: 13, opacity: 0.7 }}>PESEL: {selected.pesel}</div>}
          {selected.email && <div style={{ fontSize: 13, opacity: 0.7 }}>📧 {selected.email}</div>}
          
          <h4 style={{ marginTop: 16 }}>📄 {t('hr.contracts')}</h4>
          {(selected.contracts || []).map(c => (
            <div key={c.id} style={{
              padding: 10, borderRadius: 8, marginBottom: 8,
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
            }}>
              <div style={{ fontWeight: 600 }}>
                {CONTRACT_LABELS[c.contract_type]?.icon} {CONTRACT_LABELS[c.contract_type]?.label}
                {c.position && ` — ${c.position}`}
              </div>
              <div style={{ fontSize: 12, opacity: 0.7 }}>
                {c.start_date} → {c.end_date || '∞'} | {c.gross_salary} PLN | ZUS: {c.zus_applies ? '✅' : '❌'}
              </div>
            </div>
          ))}
          
          <button className="btn btn-primary btn-small" style={{ marginTop: 8 }} onClick={() => setShowAddContract(true)}>
            + {t('hr.new_contract')}
          </button>

          {/* Add Contract Form */}
          {showAddContract && (
            <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: 'rgba(99,102,241,0.1)' }}>
              <h4>📝 {t('hr.contract_new_for')} {selected.first_name}</h4>
              <div className="form">
                <div className="field">
                  <label>{t('hr.contract_type')}</label>
                  <select value={contractForm.contract_type} onChange={e => setContractForm(p => ({...p, contract_type: e.target.value}))}>
                    {Object.entries(CONTRACT_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v.icon} {v.label} {v.zus ? `(${t('hr.with_zus')})` : `(${t('hr.no_without_zus')})`}</option>
                    ))}
                  </select>
                </div>
                <div className="field"><label>{t('hr.position')}</label><input value={contractForm.position} onChange={e => setContractForm(p => ({...p, position: e.target.value}))} /></div>
                <div className="field"><label>{t('hr.gross_salary')}</label><input type="number" value={contractForm.gross_salary} onChange={e => setContractForm(p => ({...p, gross_salary: e.target.value}))} /></div>
                <div className="field"><label>{t('hr.start_date')}</label><input type="date" value={contractForm.start_date} onChange={e => setContractForm(p => ({...p, start_date: e.target.value}))} /></div>
                <div className="field"><label>{t('hr.end_date')}</label><input type="date" value={contractForm.end_date} onChange={e => setContractForm(p => ({...p, end_date: e.target.value}))} /></div>
                <div className="field">
                  <label>{t('hr.kup_percent')}</label>
                  <select value={contractForm.kup_rate} onChange={e => setContractForm(p => ({...p, kup_rate: parseFloat(e.target.value)}))}>
                    <option value={20}>{t('hr.kup_standard')}</option>
                    <option value={50}>{t('hr.kup_author')}</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button className="btn btn-primary" onClick={addContract}>✅ {t('hr.create')}</button>
                <button className="btn btn-secondary" onClick={() => setShowAddContract(false)}>{t('common.cancel')}</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Payroll */}
      {showPayroll && payrollData && (
        <div className="card" style={{ marginTop: 16, padding: 16 }}>
          <h3>📊 {t('hr.payroll_title')} — {payrollData.month}/{payrollData.year}</h3>
          
          {(payrollData.payrolls || payrollData.generated || []).length === 0 ? (
            <div className="empty">{t('hr.no_payroll')}</div>
          ) : (
            <>
              {(payrollData.payrolls || payrollData.generated || []).map((p, i) => (
                <div key={i} style={{
                  padding: 10, borderRadius: 8, marginBottom: 8,
                  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                }}>
                  <div style={{ fontWeight: 600 }}>{p.employee_name || p.employee}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4, fontSize: 12, marginTop: 6 }}>
                    <div>{t('hr.gross')}: <b>{p.gross?.toLocaleString()} PLN</b></div>
                    <div>{t('hr.zus_employee')}: <b>{(p.zus_employee || 0)?.toLocaleString()} PLN</b></div>
                    <div>{t('hr.pit')}: <b>{(p.pit_advance || p.pit || 0)?.toLocaleString()} PLN</b></div>
                    <div>{t('hr.net')}: <b style={{ color: '#10b981' }}>{p.net?.toLocaleString()} PLN</b></div>
                    <div>{t('hr.zus_employer')}: <b>{(p.zus_employer || 0)?.toLocaleString()} PLN</b></div>
                    <div>{t('hr.total_cost')}: <b style={{ color: '#f59e0b' }}>{(p.total_cost || 0)?.toLocaleString()} PLN</b></div>
                  </div>
                </div>
              ))}

              {payrollData.totals && (
                <div style={{
                  marginTop: 12, padding: 12, borderRadius: 10,
                  background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)',
                }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>📊 {t('hr.totals')}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, fontSize: 13 }}>
                    <div>{t('hr.salary_fund')}: <b>{payrollData.totals.gross?.toLocaleString()} PLN</b></div>
                    <div>{t('hr.to_pay_net')}: <b style={{ color: '#10b981' }}>{payrollData.totals.net?.toLocaleString()} PLN</b></div>
                    <div>{t('hr.zus_employee')}: <b>{payrollData.totals.zus_employee?.toLocaleString()} PLN</b></div>
                    <div>{t('hr.zus_employer')}: <b>{payrollData.totals.zus_employer?.toLocaleString()} PLN</b></div>
                    <div>{t('hr.pit_advance_label')}: <b>{payrollData.totals.pit?.toLocaleString()} PLN</b></div>
                    <div>{t('hr.full_cost')}: <b style={{ color: '#f59e0b' }}>{payrollData.totals.total_cost?.toLocaleString()} PLN</b></div>
                  </div>

                  {payrollData.zus_dra_summary && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>🏛️ {t('hr.zus_dra_payment')}:</div>
                      <div style={{ fontSize: 12, marginTop: 4 }}>
                        ZUS DRA: <b>{payrollData.zus_dra_summary.total_zus_payment?.toLocaleString()} PLN</b>
                        {' '}| PIT-4R: <b>{payrollData.zus_dra_summary.pit_total?.toLocaleString()} PLN</b>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
